import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SearchIcon } from 'lucide-react';
interface EditAssessmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    title: string;
    group: string;
    subjects: string[];
    tags: string[];
  }) => void;
  assessment: any;
}
export function EditAssessmentModal({
  isOpen,
  onClose,
  onSave,
  assessment
}: EditAssessmentModalProps) {
  const [title, setTitle] = useState(assessment?.title || '');
  const [group, setGroup] = useState(assessment?.group || '');
  const [subjects, setSubjects] = useState<string[]>(assessment?.subjects || assessment?.tags?.filter((tag: string) => ['Math', 'Science', 'English', 'History', 'Geography', 'Art', 'Music', 'Computer Science', 'Spanish', 'French', 'German', 'Chinese', 'Japanese'].includes(tag)) || []);
  const [tags, setTags] = useState<string[]>(assessment?.customTags || assessment?.tags?.filter((tag: string) => !['Math', 'Science', 'English', 'History', 'Geography', 'Art', 'Music', 'Computer Science', 'Spanish', 'French', 'German', 'Chinese', 'Japanese'].includes(tag)) || []);
  const [subjectInput, setSubjectInput] = useState('');
  const [tagInput, setTagInput] = useState('');
  // Predefined groups
  const groupOptions = ['Primary (Grades 4-5)', 'Primary (Grades K-3)', 'Middle School (Grades 6-8)', 'High School (Grades 9-12)', 'College/University', 'Professional Development'];
  // Predefined subjects
  const subjectOptions = ['Math', 'Science', 'English', 'History', 'Geography', 'Art', 'Music', 'Computer Science', 'Spanish', 'French', 'German', 'Chinese', 'Japanese', 'Physics', 'Chemistry', 'Biology'];
  const handleAddSubject = (subject: string) => {
    if (subject && !subjects.includes(subject)) {
      setSubjects([...subjects, subject]);
      setSubjectInput('');
    }
  };
  const handleRemoveSubject = (subject: string) => {
    setSubjects(subjects.filter(s => s !== subject));
  };
  const handleAddTag = (tag: string) => {
    if (tag && tags.length < 2 && !tags.includes(tag)) {
      setTags([...tags, tag]);
      setTagInput('');
    }
  };
  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter(t => t !== tag));
  };
  const handleSave = () => {
    onSave({
      title,
      group,
      subjects,
      tags
    });
    onClose();
  };
  const filteredSubjects = subjectOptions.filter(subject => subject.toLowerCase().includes(subjectInput.toLowerCase()) && !subjects.includes(subject));
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-1248">
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1249">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} transition={{
        duration: 0.2
      }} className="bg-surface-elevated rounded-2xl shadow-2xl max-w-lg w-full border border-custom max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-1250">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-custom" data-id="element-1251">
            <h2 className="text-xl font-bold text-text-primary" data-id="element-1252">
              Organize Assessment
            </h2>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-surface transition-colors" aria-label="Close modal" data-id="element-1253">
              <XIcon size={20} className="text-text-secondary" data-id="element-1254" />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-1255">
            {/* Description */}
            <p className="text-sm text-text-secondary" data-id="element-1256">
              Add details that help you quickly search, filter, and organize
              your assessments.
            </p>

            {/* Assessment Title */}
            <div data-id="element-1257">
              <label className="block text-sm font-semibold text-text-primary mb-2" data-id="element-1258">
                Assessment Title
              </label>
              <input type="text" value={title} onChange={e => setTitle(e.target.value)} className="w-full px-4 py-2.5 bg-surface border border-custom rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-transparent text-text-primary" placeholder="Enter assessment title..." data-id="element-1259" />
            </div>

            {/* Group */}
            <div data-id="element-1260">
              <label className="block text-sm font-semibold text-text-primary mb-2" data-id="element-1261">
                Group
              </label>
              <select value={group} onChange={e => setGroup(e.target.value)} className="w-full px-4 py-2.5 bg-surface border border-custom rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-transparent text-text-primary appearance-none cursor-pointer" data-id="element-1262">
                <option value="" data-id="element-1263">Select a group...</option>
                {groupOptions.map(option => <option key={option} value={option} data-id="element-1264">
                    {option}
                  </option>)}
              </select>
            </div>

            {/* Subject Area */}
            <div data-id="element-1265">
              <label className="block text-sm font-semibold text-text-primary mb-1" data-id="element-1266">
                Subject Area
              </label>
              <p className="text-xs text-text-secondary mb-3" data-id="element-1267">
                Choose the subject this assessment belongs to.
              </p>

              {/* Selected Subjects */}
              {subjects.length > 0 && <div className="flex flex-wrap gap-2 mb-3" data-id="element-1268">
                  {subjects.map(subject => <span key={subject} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#2563EB] bg-opacity-10 text-[#2563EB] rounded-lg text-sm font-semibold" data-id="element-1269">
                      {subject}
                      <button onClick={() => handleRemoveSubject(subject)} className="hover:bg-[#2563EB] hover:bg-opacity-20 rounded-full p-0.5 transition-colors" data-id="element-1270">
                        <XIcon size={14} data-id="element-1271" />
                      </button>
                    </span>)}
                </div>}

              {/* Subject Input */}
              <div className="relative" data-id="element-1272">
                <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary" data-id="element-1273" />
                <input type="text" value={subjectInput} onChange={e => setSubjectInput(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter' && subjectInput.trim()) {
                  e.preventDefault();
                  handleAddSubject(subjectInput.trim());
                }
              }} className="w-full pl-9 pr-4 py-2.5 bg-surface border border-custom rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-transparent text-text-primary placeholder-text-tertiary" placeholder="Add another subject..." data-id="element-1274" />
              </div>

              {/* Subject Suggestions */}
              {subjectInput && filteredSubjects.length > 0 && <div className="mt-2 bg-surface border border-custom rounded-lg shadow-custom-md max-h-40 overflow-y-auto" data-id="element-1275">
                  {filteredSubjects.map(subject => <button key={subject} onClick={() => handleAddSubject(subject)} className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-surface-elevated transition-colors" data-id="element-1276">
                      {subject}
                    </button>)}
                </div>}
            </div>

            {/* Tags */}
            <div data-id="element-1277">
              <label className="block text-sm font-semibold text-text-primary mb-1" data-id="element-1278">
                Tags (optional){' '}
                <span className="text-text-tertiary font-normal" data-id="element-1279">
                  {tags.length}/2
                </span>
              </label>
              <p className="text-xs text-text-secondary mb-3" data-id="element-1280">
                Add up to 2 tags to organize and filter.
              </p>

              {/* Selected Tags */}
              {tags.length > 0 && <div className="flex flex-wrap gap-2 mb-3" data-id="element-1281">
                  {tags.map(tag => <span key={tag} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#00B75F] bg-opacity-10 text-[#00B75F] rounded-lg text-sm font-semibold" data-id="element-1282">
                      {tag}
                      <button onClick={() => handleRemoveTag(tag)} className="hover:bg-[#00B75F] hover:bg-opacity-20 rounded-full p-0.5 transition-colors" data-id="element-1283">
                        <XIcon size={14} data-id="element-1284" />
                      </button>
                    </span>)}
                </div>}

              {/* Tag Input */}
              {tags.length < 2 && <div className="relative" data-id="element-1285">
                  <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary" data-id="element-1286" />
                  <input type="text" value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter' && tagInput.trim()) {
                  e.preventDefault();
                  handleAddTag(tagInput.trim());
                }
              }} className="w-full pl-9 pr-4 py-2.5 bg-surface border border-custom rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-transparent text-text-primary placeholder-text-tertiary" placeholder="Search or create tag..." data-id="element-1287" />
                </div>}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-custom bg-surface" data-id="element-1288">
            <button onClick={onClose} className="px-6 py-2.5 border-2 border-custom text-text-primary rounded-lg font-semibold hover:bg-surface-elevated transition-colors" data-id="element-1289">
              Cancel
            </button>
            <button onClick={handleSave} disabled={!title.trim()} className={`px-6 py-2.5 text-white rounded-lg font-semibold transition-colors shadow-sm ${title.trim() ? 'bg-[#00B75F] hover:bg-[#00a055]' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-1290">
              Save
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}